import { useEffect, useState } from "react";
import { getPromoBanners } from "../../../utils/APICalls/APICalls";
import { PromoBannerItem } from "../../../utils/promoMaterial/promoMaterialTypes";
import { useLoyaltyContext } from "../loyalty/useLoyaltyContext";
import { useAppSelector } from "../useReduxHooks";

/**
 * The home rail the loyalty service serves (story #4087): the customer's
 * applicable offers and the chain's authored banners, interleaved in one
 * display order.
 *
 * Behind `useLoyaltyBanners`, which is what makes this a replacement rather than
 * an addition — with the flag on the rail is the *only* home carousel, so a
 * restaurant flips it once its banners are authored. Off, nothing is fetched.
 */
export const usePromoBanners = () => {
  const { channel, mappedOrderType, selectedFranchiseId } = useLoyaltyContext();
  const useLoyaltyBanners = useAppSelector(
    (state) => state.config.restaurantConfig?.useLoyaltyBanners === true
  );
  /**
   * Who is asking, not what they hold.
   *
   * The response's offer half is per-*customer*, and the only thing that changes
   * the customer is signing in or out. It is deliberately not keyed on the offer
   * list settling: the rail resolves each offer card against Redux at render
   * time, so a list that changes — applying a reward, one expiring — already
   * re-renders the cards without a round trip. Refetching there cost a full rail
   * reload and a possible reorder under the customer's finger on every Apply.
   */
  const isSignedIn = useAppSelector((state) => Boolean(state.userInfo?.token));

  const [banners, setBanners] = useState<PromoBannerItem[]>([]);

  useEffect(() => {
    // The rail is per branch: core rejects a request without one (422), and
    // the page asks before the customer has picked a branch.
    if (!useLoyaltyBanners || !selectedFranchiseId) {
      return;
    }

    let current = true;

    const fetchBanners = async () => {
      try {
        const response = await getPromoBanners({
          channel,
          franchiseId: selectedFranchiseId,
          ...(mappedOrderType && { orderType: mappedOrderType }),
        });
        if (current) {
          setBanners(response.data?.banners ?? []);
        }
      } catch (error) {
        // The rail keeps whatever it last showed — nothing, on a first load. A
        // marketing carousel is never worth an error state on the home screen.
        console.error("Promo banners: request failed", error);
      }
    };

    fetchBanners();

    // Guards against a slow first response overwriting a newer one after the
    // customer changed something.
    return () => {
      current = false;
    };
    // Not gated on the offers request: the two are independent, and serialising
    // them put an empty-then-populate shift in the home hero slot that the
    // sliders this replaced never had.
  }, [
    useLoyaltyBanners,
    isSignedIn,
    channel,
    mappedOrderType,
    selectedFranchiseId,
  ]);

  return { banners, useLoyaltyBanners };
};
